/** Verified link access never inherits the signed-in account's broader permissions. */
import { useCallback, useEffect, useMemo, type ReactNode } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { clearOfflineJudgePointer } from "@/components/judge/offlineRecovery";
import { api, ApiError } from "@/api/client";
import { getIdToken, signOut } from "@/firebase";
import { RequireAuth } from "@/auth/RequireAuth";
import { useRoleMode, leaveRoleMode, hasRoleModeMarker } from "@/auth/roleMode";
import {
  judgeSessionToken,
  judgeSessionClient,
  useJudgeSession,
  type JudgeSession,
} from "@/stores/judgeSession";
import { useAuthStore } from "@/stores/auth";
import { ScorioSocket } from "@/api/ws";
import "@/pages/RoleAccess.css";

export interface RolePageContext {
  transport: typeof api;
  principalKey: string;
  getToken: () => Promise<string | null>;
  onSignOut: () => Promise<void>;
  onSessionInvalid: () => void;
  session: JudgeSession | null;
}
export function AccountOnly({ children }: { children: ReactNode }) {
  const mode = useRoleMode((s) => s.active);
  const session = useJudgeSession((s) => s.session);
  if (mode || session || hasRoleModeMarker()) return <RoleDenied />;
  return <>{children}</>;
}
export function RoleDenied() {
  const session = useJudgeSession((s) => s.session);
  return (
    <main className="role-access">
      <header>
        <strong>Scorio</strong>
      </header>
      <section className="role-access-card">
        <h1>This role cannot open this page</h1>
        <p>Use the workspace available through your role link.</p>
        <Link
          className="role-access-primary"
          to={
            session
              ? `/competition/${encodeURIComponent(session.competitionId)}/workspace?role=link`
              : "/j"
          }
        >
          Return to your role
        </Link>
      </section>
    </main>
  );
}
export function RoleRoute({
  roles,
  children,
  watchSession = true,
}: {
  roles: string[];
  watchSession?: boolean;
  children: (context: RolePageContext) => ReactNode;
}) {
  const { competitionId } = useParams();
  const stored = useJudgeSession((s) => s.session);
  const mode = useRoleMode((s) => s.active) || stored !== null || hasRoleModeMarker();
  const userId = useAuthStore((s) => s.user?.id);
  const cid = competitionId ?? stored?.competitionId ?? "";
  const transport = useMemo(
    () => (mode ? judgeSessionClient(cid, stored?.principalKey) : api),
    [mode, cid, stored?.principalKey],
  );
  const navigate = useNavigate();
  const session =
    mode && stored && stored.competitionId === cid && Date.parse(stored.expiresAt) > Date.now()
      ? stored
      : null;
  const principalKey = session?.principalKey ?? `user:${userId ?? "none"}`;
  useEffect(() => {
    if (!session || hasRoleModeMarker()) return;
    const url = new URL(window.location.href);
    url.searchParams.set("role", "link");
    window.history.replaceState(window.history.state, "", url.pathname + url.search);
  }, [session]);
  useEffect(() => {
    if (!stored) return;
    const timer = window.setTimeout(
      () => useJudgeSession.getState().clear(stored.competitionId),
      Math.min(Math.max(0, Date.parse(stored.expiresAt) - Date.now()), 2147483647),
    );
    return () => clearTimeout(timer);
  }, [stored]);
  useEffect(() => {
    if (
      !watchSession ||
      !session ||
      (session.roles.includes("judge") && !session.roles.includes("head_judge"))
    )
      return;
    const invalidate = () => {
      if (useJudgeSession.getState().session?.principalKey === session.principalKey)
        useJudgeSession.getState().clear(cid);
    };
    const baseURL =
      import.meta.env.VITE_WS_URL ?? (import.meta.env.VITE_API_URL ?? "").replace(/^http/, "ws");
    const origin =
      baseURL ||
      `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}`;
    const socket = new ScorioSocket(
      `${origin}/api/competitions/${encodeURIComponent(cid)}/${session.roles.includes("announcer") ? "announcer/ws" : "ws"}`,
      {
        getToken: async () => judgeSessionToken(cid, session.principalKey),
      },
    );
    const off = socket.onClose((code) => {
      if (code === 4401 || code === 4412) invalidate();
      else if (code === 4409) {
        // A seat takeover is not a revoked link: verify before clearing the pad's conflict flow.
        void transport
          .get(`/api/competitions/${encodeURIComponent(cid)}/live`)
          .catch((error: unknown) => {
            if (error instanceof ApiError && error.status === 401) invalidate();
          });
      }
    });
    socket.connect();
    return () => {
      off();
      socket.disconnect();
    };
  }, [cid, session, transport, watchSession]);
  const getToken = useCallback(
    async () => (mode ? judgeSessionToken(cid, principalKey) : getIdToken()),
    [mode, cid, principalKey],
  );
  const onSignOut = useCallback(async () => {
    if (!mode) {
      await signOut();
      return;
    }
    try {
      await transport.post("/api/judge-access/logout", {}, { canRetry: () => false });
    } finally {
      const current = useJudgeSession.getState().session;
      if (!current || current.principalKey === principalKey) {
        clearOfflineJudgePointer();
        useJudgeSession.getState().clear(cid);
        leaveRoleMode();
        navigate("/j", { replace: true });
      }
    }
  }, [mode, transport, cid, navigate, principalKey]);
  const onSessionInvalid = useCallback(() => {
    if (useJudgeSession.getState().session?.principalKey === principalKey)
      useJudgeSession.getState().clear(cid);
  }, [principalKey, cid]);
  const context: RolePageContext = {
    transport,
    principalKey,
    getToken,
    onSignOut,
    onSessionInvalid,
    session,
  };
  if (!mode) return <RequireAuth>{children(context)}</RequireAuth>;
  if (stored && stored.competitionId !== cid) return <RoleDenied />;
  if (!session) return <Navigate to="/j" replace />;
  if (session.competitionId !== cid || !session.roles.some((role) => roles.includes(role)))
    return <RoleDenied />;
  return <>{children(context)}</>;
}

export function AccountAuth({ children }: { children: ReactNode }) {
  return (
    <AccountOnly>
      <RequireAuth>{children}</RequireAuth>
    </AccountOnly>
  );
}
